import fetch from '../core/fetch';
import { parseResponse } from '../core/fetch/response';
import Cookie from './cookie';
import Cognito from './cognito';
import Subscriptions from './subscriptions';
import { parseZipResponse } from '../utils/fender-connect-utils';
import AuthorizationHeader from './authorization-header';

const paymentMethods = {
  PAYMENT_APPLE: 'apple',
  PAYMENT_PAYPAL: 'PayPal',
  PAYMENT_CREDIT_CARD: 'Stripe',
};

const parseState = state => {
  const washingtonDC = 'Washington DC';
  if (state && state.indexOf('District of Columbia') > -1) {
    return washingtonDC;
  }
  if (state === 'DC') {
    return washingtonDC;
  }
  return state;
};

const getUTMData = () => {
  const utm = Cookie.getUtms();
  const clickref = Cookie.getClickRef();
  const affiliateId = Cookie.getAffiliateId();
  return {
    'affiliate-id': affiliateId,
    clickref,
    'utm-campaign': utm.utm_campaign,
    'utm-medium': utm.utm_medium,
    'utm-source': utm.utm_source,
  };
};

const createSubscription = async (orderData, subscriptions) => {
  const session = await Cognito.fetchCognitoCredentials();
  const url = `${process.env.REACT_APP_CLIENT_FC_SUBSCRIPTIONS_API}/subscriptions`;
  const { play } = subscriptions || {};
  let parsedOrderData = { ...orderData, action: 'renew' };
  let method = 'PATCH';
  if (!play || play === 'unsubscribed') {
    method = 'POST';
    parsedOrderData = { ...orderData };
  }
  let params = {};
  if (session) {
    params = {
      method,
      headers: {
        Authorization: `Bearer ${session.token}`,
      },
      body: JSON.stringify(parsedOrderData),
    };
  }
  const response = await fetch(url, params);
  return response;
};

const getPayPalData = (payPalResponse, payPalDetails) => {
  const { orderID } = payPalResponse;
  const { email, id } = payPalDetails;
  return {
    city: payPalDetails['ship-to-city'],
    country: payPalDetails['country-code'],
    currency: payPalDetails['currency-code'],
    'first-name': payPalDetails['first-name'],
    'last-name': payPalDetails['last-name'],
    'paypal-ectoken': orderID,
    'paypal-baid': id,
    'paypal-email': email,
    'product-sku': process.env.REACT_APP_CLIENT_PLAY_PRODUCT_SKU,
    postal: payPalDetails['ship-to-zip'],
    state: parseState(payPalDetails['ship-to-state']),
  };
};

const getPayPalDetails = async ecToken => {
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/paypal/billing-agreement?token=${ecToken}`;
  const response = await fetch(url, {
    method: 'GET',
  });
  return parseResponse(response);
};

const getFenderConnectData = (fenderConnect, country) => {
  const { city, email, firstName, lastName, productRatePlanId, stateAbbr, userID } = fenderConnect;
  const stateDefault = country === 'CA' ? 'ON' : 'FL';
  return {
    city,
    email,
    'first-name': firstName,
    'last-name': lastName,
    'product-rate-plan-id': productRatePlanId,
    state: parseState(stateAbbr || stateDefault),
    'user-id': userID,
  };
};

const createPayPalSubscription = async (
  payPalResponse,
  fenderConnect,
  selectedPlan,
  redemptionCode = null,
) => {
  try {
    const payPalDetails = await getPayPalDetails(payPalResponse.paymentToken);
    const { price } = selectedPlan.pricing;
    const { subscriptions } = fenderConnect;
    const utmData = getUTMData();
    const fenderConnectData = getFenderConnectData(fenderConnect);
    const payPalData = getPayPalData(payPalResponse, payPalDetails);
    const lowerCasedCode = redemptionCode ? redemptionCode.toLowerCase() : null;
    const orderData = {
      ...utmData,
      ...fenderConnectData,
      ...payPalData,
      'payment-type': paymentMethods.PAYMENT_PAYPAL,
      price,
      'product-rate-plan-sku': selectedPlan['product-rate-plan-sku'],
      'product-rate-plan-id': selectedPlan['product-rate-plan-id'] || null,
      'redemption-code': lowerCasedCode,
    };
    return createSubscription(orderData, subscriptions);
  } catch (error) {
    return Promise.resolve(error);
  }
};

const getCardData = card => {
  const { address_zip, brand, country, exp_month, exp_year, id, last4, name } = card;
  return {
    country,
    'credit-card-expire-month': exp_month,
    'credit-card-expire-year': exp_year,
    'credit-card-holder-name': name,
    'credit-card-number': last4,
    'stripe-card-token-id': id,
    'credit-card-type': brand,
    postal: address_zip,
  };
};

const getStripeCustomerToken = async id => {
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/stripe/customers`;
  const headers = await AuthorizationHeader.getCognitoHeader();
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ token: id }),
    ...headers,
  });
  if (response.status >= 200 && response.status < 300) {
    return parseResponse(response);
  }
  return Promise.reject(response);
};

const getStripeData = async ({ token }) => {
  const { card, id } = token;
  const stripeCustomerToken = await getStripeCustomerToken(id).catch(error => error);
  const cardData = getCardData(card);
  return {
    ...cardData,
    'stripe-customer-token-id': stripeCustomerToken.id,
  };
};

const getPlanData = plan => {
  const { pricing } = plan;
  const { currency, price } = pricing;
  return {
    currency,
    price,
    'product-rate-plan-sku': plan['product-rate-plan-sku'],
  };
};

const getPromoDiscount = (promoCode, price) => {
  const { message } = promoCode || {};
  if (message !== 'valid') {
    return 0;
  }
  const percentage = promoCode['discount-percentage'] * 0.01;
  return parseFloat(percentage * price).toFixed(2);
};

const getState = async ({ postal, country }) => {
  const zipResponse = await Subscriptions.getCityStateZippopotamus(postal, country);
  const { stateAbbr } = parseZipResponse(zipResponse) || {};
  const defaultStateAbbr = country === 'CA' ? 'ON' : 'FL';
  return stateAbbr || defaultStateAbbr;
};

const formatStripeOrder = async (
  paymentMethod,
  token,
  fenderConnect,
  plan,
  redemptionCode = '',
) => {
  const stripeData = await getStripeData(token);
  const fenderConnectData = getFenderConnectData(fenderConnect, stripeData.country);
  const planData = getPlanData(plan);
  const utmData = getUTMData();
  const state = await getState(stripeData);
  const lowerCasedCode = redemptionCode ? redemptionCode.toLowerCase() : '';
  return {
    ...stripeData,
    ...fenderConnectData,
    ...planData,
    ...utmData,
    state: parseState(state),
    'payment-type': paymentMethod,
    'product-sku': process.env.REACT_APP_CLIENT_PLAY_PRODUCT_SKU,
    'redemption-code': lowerCasedCode,
  };
};

const getPayPalToken = async () => {
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/paypal/express-checkout-token`;
  const response = await fetch(url, {
    method: 'GET',
  });
  return parseResponse(response);
};

export {
  createPayPalSubscription,
  createSubscription,
  formatStripeOrder,
  getCardData,
  getFenderConnectData,
  getPayPalData,
  getPayPalDetails,
  getPayPalToken,
  getPromoDiscount,
  getStripeCustomerToken,
  getUTMData,
  parseState,
};
export default paymentMethods;
